import { useTranslation } from 'react-i18next';
import { Box, styled, Theme, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { getUserEngagementCopy, NA } from '../constants';
import { colorMap } from 'src/components/dashboard-pages/solve-insights/helpers';
import { Conversation } from 'src/services/apiInterfaces';

type Variant = 'positive' | 'negative' | 'mixed' | 'default';

const metricToVariant = (
  metric: Conversation['dropoff_rating'] | Conversation['relevance_rating'],
): Variant => {
  if (typeof metric === 'boolean') {
    return metric ? 'negative' : 'positive';
  }

  switch (metric) {
    case 'relevant':
      return 'positive';
    case 'irrelevant':
      return 'negative';
    case 'somewhat relevant':
      return 'mixed';
    default:
      return 'default';
  }
};

const variantToBgColor = ({
  deflectionVariant,
  theme,
}: {
  deflectionVariant: Variant;
  theme: Theme;
}) => {
  const colorMapping = colorMap(theme.palette);
  switch (deflectionVariant) {
    case 'positive':
      return colorMapping.deflection.positive;
    case 'negative':
      return colorMapping.deflection.negative;
    case 'mixed':
      return colorMapping.deflection.mixed;
    default:
      return colorMapping.na;
  }
};

const metricToLabel = (
  metric: Conversation['dropoff_rating'] | Conversation['relevance_rating'],
) => {
  if (typeof metric === 'boolean') {
    const userEngagementCopy = getUserEngagementCopy();
    return metric ? userEngagementCopy.no : userEngagementCopy.yes;
  }

  return metric;
};

export const DeflectionInsightCell = ({
  isProcessing = false,
  metric,
}: {
  isProcessing?: boolean;
  metric: Conversation['dropoff_rating'] | Conversation['relevance_rating'];
}) => {
  const { palette } = useTheme();
  const { t } = useTranslation('intent-conversation-analytics');

  if (!isProcessing && (metric === null || metric === undefined)) {
    return (
      <Typography color={palette.colors.grey[600]} variant='font14'>
        {NA}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
      }}
    >
      <DeflectionBadge deflectionVariant={metricToVariant(metric)}>
        <Typography color={palette.colors.white} noWrap variant='font11'>
          {isProcessing
            ? t('sentimentInsightCell.processing')
            : metricToLabel(metric)}
        </Typography>
      </DeflectionBadge>
    </Box>
  );
};

const DeflectionBadge = styled('div')<{ deflectionVariant: Variant }>`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 4px;
  text-transform: capitalize;
  background-color: ${variantToBgColor};
  border-radius: 4px;
`;
